import type { Partner } from "@expert/shared-types";
import type { EvlConnectionPayload } from "@expert/websocket";
import type { JsonValue, Promisable } from "type-fest";

import { getAppConfigSingleton } from "@expert/app-config";
import { getLogger } from "@expert/logging";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";

import type { CaseDetails } from "@/shared-types";

import { getRootDispatcher, updateDispatcherPartner } from "@/analytics";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

import type { AgentSdk } from "./agentSdk";
import type { Task } from "./task";
import type {
    AdvanceReadStatusOptions,
    AgentActivity,
    CallbackState,
    MutableAgentAttribute,
    ServiceProvider,
    SurveyMeta,
    TaskCancelledReason,
    TaskCompletedReason,
    TaskStatusReason,
    WrappingEndReason,
    WrappingStartReason,
} from "./types";
import type { ConferenceDetails, UpdateParticipantOptions, VoiceTask } from "./voice";

import { getSelectedSession } from "../sessions";
import { useCallbackStore } from "../sessions/callback.store";
import { useSessionStore } from "../sessions/session.store";
import { sessionsOrchestrator } from "../sessions/sessionsOrchestrator";
import { scheduleCallbackNow } from "./callbacks";
import { type ChatMessage, isChatTask } from "./chat";
import { TASK_COMPLETE_REASON } from "./config";
import { sdkEventBus } from "./eventBus";
import { callEventsHandler, conferenceEventHandler } from "./events";
import { useAgentStore } from "./store";
import { isVoiceTask } from "./voice";

const logger = getLogger({
    module: "agentSdkBase",
});

export abstract class AgentSdkBase<TTask extends Task = Task> implements AgentSdk<TTask> {
    abstract readonly sdkType: string;
    abstract readonly requiresEvl: boolean;
    private lastConnectedSendTime = Date.now();
    constructor() {
        this.subscribeToWebSocketEvents();
    }
    public getSdkTypeForTask = (_task: Task): string => this.sdkType;

    // Expert
    public abstract agentActivities: readonly AgentActivity[];
    public abstract availableAgentActivity: AgentActivity;
    public abstract offlineAgentActivity: AgentActivity;
    public abstract defaultAgentActivity?: AgentActivity;
    public abstract isAgentActivitySelectable(activity: AgentActivity): boolean;
    public abstract setAgentActivity(activity: AgentActivity, activityUpdatedAt?: number): Promise<void>;
    public abstract getAgentActivity(): AgentActivity;
    public abstract getPartners(): ReadonlySet<Partner>;
    public abstract hasActivitySelfManagement?: boolean;
    public abstract updateAgentAttributes(attributes: Record<MutableAgentAttribute, JsonValue>): Promise<void>;
    public abstract serviceProvider: ServiceProvider;

    // Task
    public abstract canHandleTaskType(task: Task): boolean;

    public abstract acceptTask(task: TTask): Promise<void>;

    public abstract rejectTask(task: TTask): Promise<void>;

    public abstract wrapupTask(task: TTask, reason: string): Promise<void>;

    public abstract completeTask(task: TTask, reason: TaskCompletedReason): Promise<void>;

    public abstract cancelTask(task: TTask): Promise<void>;

    public abstract fetchContactsForTask(task: TTask): Promise<void>;

    // Voice
    public abstract hangupCall(task: TTask, reason: string): Promise<void>;

    public abstract areSurveysAutomatic(task: TTask): boolean;

    public abstract sendToSurvey(task: TTask, surveyMeta?: SurveyMeta): Promise<void>;

    public abstract holdCall(task: TTask): Promise<void>;

    public abstract resumeCall(task: TTask): Promise<void>;

    public abstract muteCall(task: TTask, shouldMute: boolean): Promise<void>;

    public completeSession(partner: Partner) {
        sessionsOrchestrator.completeSession(this, partner);
    }

    public async scheduleCallbackNow(callbackDelay: number, callbackMdn: string) {
        return await scheduleCallbackNow(
            this,
            callbackDelay,
            callbackMdn,
            this.callbackPendingAgentActivity,
            this.wrappingAgentActivity,
        );
    }

    // Callback
    public async callCustomerBack(callbackState: CallbackState): Promise<void> {
        const sessionLogger = logger.child({ action: "callCustomerBack", callbackState });
        sessionLogger.trace("Call Customer Back | execution started");

        sessionLogger.info("Call Customer Back | creating customer callback for task...");
        const newTaskSid = await this.createCustomerCallbackForTask(callbackState);
        sessionLogger.info({ newTaskSid }, "Call Customer Back | New Task Sid created");

        void getRootDispatcher()
            .withExtra({
                partner: callbackState.originalTask.partner,
            })
            .withIdentities({
                TaskSid: newTaskSid,
                PreviousTaskSid: callbackState.originalTask.id,
            })
            .dispatchBusinessEvent("OutboundCallInitiated", {
                destination: callbackState.callbackMDN,
                outboundCallType: "TollFreeNumber",
            })
            .then(() => {
                sessionLogger.info(
                    { analyticsEventName: "OutboundCallInitiated" },
                    "Call customer | OutboundCallInitiated analytics dispatched",
                );
            });
    }

    public async callCustomer(mdn: string, { extension }: { extension?: string } = {}): Promise<void> {
        // TODO: guard against ad-hoc call while in a call
        // TODO: guard against ad-hoc call while in certain activities (ie. lunch, break....etc) (Get info from workforce)
        const localLogger = logger.child({ action: "callCustomer" });

        // TODO: Add fn parameter to the message in logger
        localLogger.trace("Call customer | execution started");

        // TODO: rename to current task
        const previousTask = getSelectedSession().currentTask;
        const taskLogger = localLogger.child(previousTask?.toLog() ?? {});

        taskLogger.info("Call customer | creating outbound call...");
        startDynatraceAction("OutboundCall");
        const newTaskSid = await this.createOutboundCall(mdn);
        taskLogger.info("Call customer | outbound call created");

        void getRootDispatcher()
            .withExtra({
                partner: previousTask?.partner,
            })
            .withIdentities({
                TaskSid: newTaskSid,
                PreviousTaskSid: previousTask?.id,
            })
            .dispatchBusinessEvent("OutboundCallInitiated", {
                destination: mdn,
                outboundCallType: "TollFreeNumber",
            })
            .then(() => {
                taskLogger.info(
                    { analyticsEventName: "OutboundCallInitiated" },
                    "Call customer | OutboundCallInitiated analytics dispatched",
                );
            });

        if (extension === undefined) return;

        const unsubscribe = expertWorkspaceWebSocketEventBus.on(
            "ws_conference-event",
            ({ callDirection, eventType, taskId }: { callDirection?: string; eventType: string; taskId: string }) => {
                if (callDirection === "outbound" || eventType !== "participant-join" || taskId !== newTaskSid) return;
                unsubscribe();
                void this.sendDtmfDigits(extension);
            },
        );
    }

    // Case Details
    public getCaseDetails(task: Task): CaseDetails | undefined {
        return task.caseDetails;
    }

    public async updateCaseDetails(task: Task, caseDetailsUpdates: Partial<CaseDetails>): Promise<void> {
        return await task.updateCaseDetails(caseDetailsUpdates);
    }

    public abstract sendDtmfDigits(digits: string): Promise<void>;

    // Call Redirect & Transfer
    public abstract redirectCallToTFN(task: TTask, redirectTo: string): Promise<void>;
    public abstract redirectCallToSIP(task: TTask, redirectTo: string): Promise<void>;

    // Conference
    public abstract addConferenceParticipant(
        task: TTask,
        name: string,
        to: string,
        isSip?: boolean,
        sipParams?: Record<string, string>,
    ): Promise<void>;

    public abstract updateParticipant(task: TTask, callId: string, options: UpdateParticipantOptions): Promise<void>;

    public abstract removeParticipant(task: TTask, callId: string): Promise<void>;

    public abstract removePendingParticipant(task: TTask, callId: string): Promise<void>;

    public abstract leaveConference(task: TTask): Promise<void>;

    public abstract endConference(task: TTask): Promise<void>;

    public abstract resetConferenceState(task: TTask): Promise<void>;

    /**
     * Start (or extend) a conference by dialing a known queue / quick-connect ARN directly, without
     * discovering the endpoint through the agent's routing profile. Enables the audio-disclosure (and
     * similar) conference when the target is not surfaced via getEndpoints.
     */
    public abstract startConferenceWithEndpointArn(task: TTask, endpointARN: string): Promise<void>;

    // Chat

    public abstract getMessages(task: TTask): Promise<ChatMessage[]>;

    public abstract sendMessage(task: TTask, message: string): Promise<void>;

    public abstract sendTypingIndicator(task: TTask): Promise<void>;

    public abstract advanceReadStatus(task: TTask, options: AdvanceReadStatusOptions): Promise<void>;

    // Private/protected
    private subscribeToWebSocketEvents() {
        expertWorkspaceWebSocketEventBus.on("ws_conference-event", conferenceEventHandler);
        expertWorkspaceWebSocketEventBus.on("ws_call-event", callEventsHandler);
        expertWorkspaceWebSocketEventBus.on("ws_evl-connection-success", (payload: EvlConnectionPayload) => {
            this.lastConnectedSendTime = payload.sendTime;
        });
        expertWorkspaceWebSocketEventBus.on("ws_tenant-disconnected", (payload: EvlConnectionPayload) => {
            // compare payload.sendTime to lastConnectedSendTime, only send if disconnect happens after lastConnectedSendTime
            if (payload.sendTime < this.lastConnectedSendTime) {
                // do nothing, websocket message was sent before reconnect
                logger.info("Websocket evl connection events came in out of order.");
            } else {
                sdkEventBus.emit("evl_disconnected", true);
            }
        });
    }
    protected onInitialized(
        agentId: string,
        agentName: string,
        activity: AgentActivity,
        activityUpdatedAt: number,
        tasks: TTask[],
        serviceProvider: "twilio" | "connect" | "mock",
        pendingActivity?: AgentActivity,
    ) {
        this.serviceProvider = serviceProvider;
        const agentStore = useAgentStore.getState();
        agentStore.initAgent(agentId, agentName, activity, activityUpdatedAt, pendingActivity);

        sessionsOrchestrator.initSessions(this, tasks);

        sdkEventBus.emit("sdk_initialized", { agentId, tasks });

        const hasActiveTasks = tasks.some((task) => ["assigned", "pending"].includes(task.status));

        const orphanedTaskCompletions: Promise<void>[] = [];

        tasks.forEach((task) => {
            // If a task is in wrapping and there is no wrapping state, create one for the task
            if (task.status === "wrapping") {
                const session = useSessionStore.getState().getSessionByTaskId(task.id);

                if (!session) {
                    logger.debug({ taskId: task.id }, "Wrapping task has no session - completing");
                    orphanedTaskCompletions.push(this.completeTask(task, TASK_COMPLETE_REASON));
                } else if (!session.wrappingState) {
                    void this.startWrapup("UnexpectedWrappingTask", task);
                }
            }

            // If we already have a pending task that has not been accepted after reload, we need to accept it
            if (task.status === "pending" && task.autoAccept) {
                void this.acceptPendingTask(task);
            }
        });

        if (orphanedTaskCompletions.length > 0) {
            void Promise.allSettled(orphanedTaskCompletions).then((results) => {
                results.forEach((result) => {
                    if (result.status === "rejected") {
                        logger.warn({ error: result.reason }, "Failed to complete orphaned wrapping task");
                    }
                });
            });
        }

        const currentSession = useSessionStore.getState().computed.selectedSession();
        const { callbackState } = useCallbackStore.getState();
        const isWaitingCallback = callbackState?.callbackType === "CallbackNow";

        // If we are in an unselectable activity, and we are not wrapping or waiting for a callback,
        // and we don't have active tasks, then correct agent activity
        if (
            this.isAgentInBadActivity(activity) &&
            !currentSession?.wrappingState &&
            !isWaitingCallback &&
            !hasActiveTasks
        ) {
            void this.setAgentActivity(this.defaultAgentActivity ?? this.offlineAgentActivity, Date.now());
        } else if (hasActiveTasks && activity === this.offlineAgentActivity) {
            // If we have active chat tasks and the agent is in offline activity, set them to available
            const hasActiveChatTasks = tasks.some(
                (task) => isChatTask(task) && ["assigned", "pending"].includes(task.status),
            );
            if (hasActiveChatTasks) {
                logger.info("Setting agent from offline to available: active chat tasks after page reload");
                void this.setAgentActivity(this.availableAgentActivity, Date.now());
            }
        }
    }

    /** Is the agent in an unselectable activity while not having any assigned tasks. */
    protected isAgentInBadActivity(activity: AgentActivity) {
        if (this.isAgentActivitySelectable(activity)) {
            return false;
        }

        if (useSessionStore.getState().computed.selectedTasks().length > 0) {
            return false;
        }

        return true;
    }

    protected onActivityChanged(activity: AgentActivity, updatedAt: number) {
        const { activity: previousActivity } = useAgentStore.getState();
        useAgentStore.getState().setAgentActivity(activity, updatedAt);

        const task = getSelectedSession().currentTask;
        logger.info({ previousActivity, activity, updatedAt, task }, "onActivityChanged | Task state");
        sdkEventBus.emit("agent_activity_changed", { previousActivity, activity, updatedAt });
    }

    protected onHangupCall(task: VoiceTask) {
        const taskLogger = logger.child({ action: "onHangupCall", ...task.toLog() });
        taskLogger.trace("on Hangup Call | Execution started");
        if (task.status === "pending" && task.callDirection === "outbound") {
            // We hung up before accepting the call, which is rejecting the call
            sdkEventBus.emit("call_rejected", task);
        }
        taskLogger.trace("on Hangup Call | Execution ended");
    }

    protected onConferenceStateReset(task: VoiceTask, conferenceDetails: ConferenceDetails) {
        useSessionStore.getState().resetConferenceState(task.id, conferenceDetails);
    }

    protected markConferenceStarted(task: VoiceTask) {
        useSessionStore.getState().setConferenceStarted(task.id, true);
    }

    /**
     * To be called when a new task is created. Handles session orchestration( adding task to the session store ), event emission,
     * subscription setup, and auto-acceptance in the correct order.
     *
     * @param task - The task that was created
     * @param setupSubscriptionsHandler - Optional callback to set up event subscriptions
     *                                     after task creation but before auto-acceptance.
     *                                     This ensures subscriptions are ready to capture
     *                                     any events that occur during the accept process.
     */
    protected async onTaskCreated(task: TTask, setupSubscriptionsHandler?: (task: TTask) => Promisable<void>) {
        const taskLogger = logger.child({ action: "onTaskCreated", ...task.toLog() });
        taskLogger.trace("on Task Created | Execution started");
        sessionsOrchestrator.addNewAgentTask(task, this);

        startDynatraceAction(`Incoming${task.type}`, task.id);

        sdkEventBus.emit("task_created", task);

        await setupSubscriptionsHandler?.(task);

        if (task.status === "pending" && task.autoAccept) {
            await this.acceptTask(task);
        }
        taskLogger.trace("on Task Created | Execution ended");
    }

    protected async onTaskAccepted(task: Task) {
        const taskLogger = logger.child({ action: "onTaskAccepted", ...task.toLog() });
        taskLogger.trace("on Task Accepted | Execution started");

        updateDispatcherPartner(task.partner);

        useSessionStore.getState().updateTaskStatus(task.id, "assigned");
        sdkEventBus.emit("task_assigned", task);
        if (task.onAcceptStatus) {
            await this.setAgentActivity(task.onAcceptStatus);
        }
        await task.onAccepted?.();

        endDynatraceAction(`Incoming${task.type}`, { taskId: task.id });

        taskLogger.trace("on Task Accepted | Execution ended");
    }

    protected onTaskRejected(task: Task) {
        const taskLogger = logger.child({ action: "onTaskRejected", ...task.toLog() });
        taskLogger.trace("on Task Rejected | Execution started");
        sessionsOrchestrator.onTaskRejected(task, this);

        endDynatraceAction(`Incoming${task.type}`, { taskId: task.id, error: `Incoming${task.type}Rejected` });

        sdkEventBus.emit("task_rejected", task);
        taskLogger.trace("on Task Rejected | Execution ended");
    }

    protected async onTaskWrapping(task: Task, reason?: TaskStatusReason) {
        const taskLogger = logger.child({ action: "onTaskWrapping", ...task.toLog() });
        sdkEventBus.emit("task_wrapping", task);
        const { callbackState } = useCallbackStore.getState();

        taskLogger.trace({ callbackState }, "on Task Wrapping | Execution started");
        useSessionStore.getState().updateTaskStatus(task.id, "wrapping", reason);
        try {
            taskLogger.trace("on Task Wrapping | Determining if task should be completed");
            // On a callback now we must complete the task and set our state into Callback Pending during wrap
            if (callbackState?.callbackType === "CallbackNow") {
                taskLogger.trace({ callbackState }, "on Task Wrapping | Initiating callback flow");

                taskLogger.trace({ callbackState: callbackState }, "onTaskWrapping Callback state");

                await this.setAgentActivity(this.callbackPendingAgentActivity);
                await this.completeTask(task as TTask, "CallbackInitiated");
            } else {
                taskLogger.trace("on Task Wrapping | Starting wrapup flow");
                await this.startWrapup("TaskWrapping", task);
            }
        } catch (error) {
            taskLogger.warn({ error }, "Error in onTaskWrapping");
        }
        taskLogger.trace("on Task Wrapping | Execution ended");
    }

    protected async onTaskComplete(task: Task, reason: TaskCompletedReason) {
        const taskLogger = logger.child({ action: "onTaskComplete", ...task.toLog() });
        const { callbackState } = useCallbackStore.getState();
        const agentActivity = this.getAgentActivity();
        taskLogger.trace({ reason }, "on Task Complete | Execution started");
        sessionsOrchestrator.onTaskCompleted(task, reason, this);
        sdkEventBus.emit("task_completed", { task, reason });

        if (callbackState?.callbackType === "CallbackNow" && agentActivity !== this.callbackPendingAgentActivity) {
            logger.debug({ callbackState }, "onTaskComplete Callback state");

            // If we are in a callback now flow, we should ensure the agent activity is callback pending
            await this.setAgentActivity(this.callbackPendingAgentActivity);
        }
        taskLogger.trace("on Task Complete | Execution ended");
    }

    protected onTaskCancelled(task: Task, reason: TaskCancelledReason) {
        const taskLogger = logger.child({ action: "onTaskCancelled", ...task.toLog() });
        taskLogger.trace("on Task Cancelled | Execution started");

        sessionsOrchestrator.onTaskCancelled(task, this, reason);

        endDynatraceAction(`Incoming${task.type}`, { taskId: task.id, error: `Incoming${task.type}Cancelled` });

        sdkEventBus.emit("task_cancelled", task);

        taskLogger.trace("on Task Cancelled | Execution ended");
    }

    protected onTaskError(task: Task | undefined, error?: unknown) {
        const errorLogger = logger.child({ action: "onTaskError", taskId: task?.id });
        errorLogger.info("Task error occurred");
        sessionsOrchestrator.onTaskError(task, this, error);
    }

    protected onCallAccepted(task: VoiceTask) {
        sdkEventBus.emit("call_accepted", task);
    }

    /**
     * Called when the customer's leg drops unexpectedly while the agent is still connected and the
     * agent did not initiate the end of the call. Provider SDKs own the (provider-specific) detection
     * and delegate here; the UI layer owns the resulting side effect (the disconnect chime) by
     * subscribing to `call_customer_dropped`. See `useCallDisconnectedChime`.
     */
    protected onCustomerDropped(task: VoiceTask) {
        sdkEventBus.emit("call_customer_dropped", task);
    }

    protected onHoldCall(task: VoiceTask) {
        useSessionStore.getState().setHold(task.id, true);
        sdkEventBus.emit("call_on_hold", task);
    }

    protected onResumeCall(task: VoiceTask) {
        useSessionStore.getState().setHold(task.id, false);
        sdkEventBus.emit("call_resumed", task);
    }

    protected onMuteCall(task: VoiceTask, shouldMute: boolean) {
        useSessionStore.getState().muteTask(task.id, shouldMute);
        // TODO: Consider having one event for toggle-able events and pass the state to the event payload
        sdkEventBus.emit(shouldMute ? "call_muted" : "call_unmuted");
    }

    protected async acceptPendingTask(task: TTask) {
        const taskLogger = logger.child({ action: "acceptPendingTask", ...task.toLog() });
        if (task.status !== "pending") {
            // TODO: remove this log
            taskLogger.info("Accept Pending Task | Attempted to accept a task that is not pending");
            return;
        }

        if (isVoiceTask(task) && task.callDirection === "inbound") {
            try {
                taskLogger.trace("Accept Pending Task | Attempting to accept the task...");
                await this.acceptTask(task);
                taskLogger.trace("Accept Pending Task | Successfully accepted the task.");
            } catch (error: unknown) {
                taskLogger.info({ error }, "Accept Pending Task | Failed to accept the task with error: ");
            }
        } else {
            taskLogger.trace("Accept Pending Task | Accepting incoming call without waiting.");
            await this.acceptTask(task);
        }
    }

    public async startWrapup(reason: WrappingStartReason, task: Task) {
        const startWrapupLogger = logger.child({ action: "startWrapup", taskId: task.id, reason });
        startWrapupLogger.trace("Starting wrapup");
        const { maxSummaryGenerationTime } = getAppConfigSingleton().getPartnersConfig(task.partner);
        const duration =
            isVoiceTask(task) && maxSummaryGenerationTime ? maxSummaryGenerationTime : task.wrappingDuration;

        const session = useSessionStore.getState().getSessionByTaskId(task.id);

        if (!session) {
            throw new Error(`Cannot find session for task ${task.id}`);
        }
        const existingWrappingState = session.wrappingState;
        if (existingWrappingState) {
            startWrapupLogger.warn(
                { existingReason: existingWrappingState.startReason, newReason: reason },
                "Overwriting existing wrapping state",
            );
        }

        useSessionStore
            .getState()
            .setWrappingState({ expirationTimestamp: Date.now() + duration, startReason: reason }, session.id);

        if (!isChatTask(task)) {
            const { wrappingAgentActivity } = this;
            if (wrappingAgentActivity) await this.setAgentActivity(wrappingAgentActivity);
        }

        void getRootDispatcher().dispatchBusinessEvent("WrappingStarted", {
            wrappingStartReason: reason,
        });
    }

    /**
     * End current wrapping state
     *
     * @param reason - The reason, or trigger, for why we are ending wrapping
     * @param partner - The partner associated with the wrapping task
     * @param sessionId - The session ID to find the session. Required because callback failures can
     *                    leave us without a valid task, but we still need to complete the session wrapping.
     * @param task - The task that should exit the wrapping state (optional). When provided, the session
     *               is looked up via task.id first, otherwise sessionId is used.
     */
    public async endWrapup(reason: WrappingEndReason, partner: Partner, sessionId: string, task?: Task) {
        const sessionStore = useSessionStore.getState();
        const currentSession = task ? sessionStore.getSessionByTaskId(task.id) : sessionStore.getSession(sessionId);
        const endWrapupLogger = logger.child({
            action: "endWrapup",
            reason,
            taskId: task?.id,
            sessionId,
            ...currentSession?.toLog(),
        });

        if (!currentSession) {
            endWrapupLogger.warn("Cannot find session for task");
            throw new Error(`Cannot find session for task ${task?.id} or sessionId ${sessionId}`);
        }

        endWrapupLogger.trace({ reason }, "Ending wrapup");

        void getRootDispatcher().dispatchBusinessEvent("WrappingEnded", {
            wrappingEndReason: reason,
        });

        if (!currentSession.wrappingState && !currentSession.currentTask) {
            endWrapupLogger.warn("End Wrapping | Attempted to end wrapping without a wrapping state");
            return;
        }

        useSessionStore.getState().clearWrappingState(currentSession.id);
        try {
            if (currentSession.currentTask) {
                const completionReason = reason === "CallbackScheduled" ? "CallbackInitiated" : TASK_COMPLETE_REASON;
                endWrapupLogger.trace({ completionReason }, "on endWrapup | Completing task");
                const { callbackState } = useCallbackStore.getState();
                if (callbackState?.callbackType === "CallbackNow") {
                    await this.setAgentActivity(this.callbackPendingAgentActivity);
                }
                await this.completeTask(currentSession.currentTask as TTask, completionReason);
            } else if (reason !== "CallbackScheduled") {
                endWrapupLogger.trace({ reason }, "on endWrapup | Completing session not CallbackScheduled");
                sessionsOrchestrator.completeSession(this, partner, sessionId);
            }
        } catch (err) {
            endWrapupLogger.warn({ err }, "Error completing task or session. Manually cleaning up.");
            if (reason !== "CallbackScheduled") {
                sessionsOrchestrator.completeSession(this, partner, sessionId);
            }
        }
    }

    protected abstract createCustomerCallbackForTask(callbackState: CallbackState): Promise<string>;

    protected abstract createOutboundCall(callbackMdn: string): Promise<string>;

    protected get isCallbackNowEnabled() {
        const {
            features: { callbackEnabled },
        } = this.getPartnerConfig();
        return callbackEnabled;
    }

    protected get callbackPendingAgentActivity() {
        const { callbackPendingAgentActivity = this.offlineAgentActivity } = this.getPartnerConfig();
        return callbackPendingAgentActivity;
    }

    protected get wrappingAgentActivity() {
        const { wrappingAgentActivity } = this.getPartnerConfig();
        return wrappingAgentActivity;
    }

    protected getPartnerConfig() {
        const [partner = "unknown"] = this.getPartners();
        return getAppConfigSingleton().getPartnersConfig(partner);
    }
}
